import React, { useEffect, useMemo, useRef, useState } from 'react';
import NextGameNudge from './NextGameNudge';
import { PartnerPromoCard } from './PartnerPromo';
import { matchGuess, suggest } from '../utils/gamexMatch';
import { ntgSrShareText } from '../utils/ntgShare';
import { saveNTGSrDailyResult, stampNTGSrPuzzleUsed, saveNTGSrProgress, getNTGSrProgress, clearNTGSrProgress, getNTGSrExits, bumpNTGSrExits } from '../utils/dailyChallenge';
import BustSilhouette from './BustSilhouette';
import GameTopBar from './GameTopBar';
import SubmitSrModal from './SubmitSrModal';
import ScoreBoard from './ScoreBoard';
import nflCsvUrl from '../data/players_with_photos.csv';
import nbaCsvUrl from '../data/nbaplayers_with_photos.csv';
import moshCsvUrl from '../data/notablealumni_with_photos.csv';

// Name That Guy Sr. (born as the Game X prototype). Phase 2 wiring in
// progress: puzzle now loads from ntg_sr_puzzles (read-only RPC below);
// streak save / used_on stamping / guest claim land with the rest of the
// Sr. daily flow. CSV assets feed the autocomplete assist only.

// Puzzle now comes from ntg_sr_puzzles via the fetch_todays_ntg_sr_puzzle RPC
// (SECURITY DEFINER; 3-tier: scheduled -> used today -> next in queue). The
// fetch is read-only - nothing is stamped until the result is saved.
// Hint ladder (Matt, launch design): opener free -> wrong 1 reveals COLLEGE ->
// wrong 2 reveals THE RESUME (career_shape + resume rendered together as one
// box; both DB columns stay as-is, combined at render only) -> wrong 3 = loss.
const rowToPuzzle = (row) => ({
  id: row.id,
  opener: row.opener,
  // Optional small-print line under the opener: asterisks, scope qualifiers
  // ("emphasis: on-field", "regular season only"). Plain text on purpose - the
  // opener is rendered as text, not HTML, so a puzzle can never inject markup.
  openerNote: row.opener_note || null,
  // Optional byline shown under the puzzle card ("Crafted by... Name"). Set on
  // puzzles that came from a reader submission - see ntg_sr_submissions.
  builtBy: row.built_by || null,
  hints: [
    { label: 'College', text: row.college },
    { label: 'The Résumé', text: `${row.career_shape}. ${row.resume}` },
  ],
  answer: row.answer,
  aliases: row.aliases || [],
  photoUrl: row.photo_url,
  // Optional link under the answer on the reveal screens (won or lost):
  // "an infomercial icon for a generation..." -> the Nutrisystem ad. Only
  // rendered when both parts are set; the DB constrains the URL to http(s).
  revealLink: row.reveal_link_url && row.reveal_link_label
    ? { url: row.reveal_link_url, label: row.reveal_link_label }
    : null,
  // Which pool the answer comes from, so the guess space is bounded.
  mode: row.mode || null,
});

const MAX_GUESSES = 3;

// Copied from ResultsScreen's NTG win rain (win-only there; win-only here).
// The .football-rain / .football-drop classes are global App.css - no import.
const RAIN_EMOJIS = ['🏈', '🏀', '⚾', '⚽', '🏒', '⛳', '🎾'];

const GameX = ({ puzzleRow, started, onBegin, currentUser, claimedStreak, onGoHome, onShowAuth, onDailyComplete, onPlayDaily }) => {
  const [universe, setUniverse] = useState([]);
  const [input, setInput] = useState('');
  const [wrongGuesses, setWrongGuesses] = useState([]);
  const [status, setStatus] = useState('playing'); // playing | won | lost
  const [wonInfo, setWonInfo] = useState(null);
  const [flash, setFlash] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [savedResult, setSavedResult] = useState(null);
  const [showSubmit, setShowSubmit] = useState(false);
  const inputRef = useRef(null);
  const savedRef = useRef(false);

  // App fetches the row (same pattern as Jr.); this component only renders it.
  const puzzle = useMemo(() => (puzzleRow ? rowToPuzzle(puzzleRow) : null), [puzzleRow]);

  // Resume a puzzle that was left mid-game: restore the misses (which also
  // restores the revealed hints and the header's miss boxes, since both derive
  // from wrongGuesses). Runs once per mount, only while still playing, and only
  // for the same puzzle on the same Pacific day - getNTGSrProgress enforces both.
  const resumedRef = useRef(false);
  useEffect(() => {
    if (resumedRef.current) return;
    if (!puzzle || status !== 'playing') return;
    resumedRef.current = true;
    const saved = getNTGSrProgress(puzzle.id);
    // Guard the >= MAX case: a finished board is handled by the played-today
    // lock, so never resume straight into a loss.
    if (saved && saved.wrongGuesses.length < MAX_GUESSES) {
      setWrongGuesses(saved.wrongGuesses);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [puzzle]);

  // Persist the result exactly once when the game ends: stamp the puzzle used
  // (fire-and-forget, like Jr.'s stampNTGQuestionUsed) then save streak ->
  // ironman SEQUENTIALLY via saveNTGSrDailyResult. hintsUsed feeds the share
  // tier and is stored for the DAILY_LOCKED share later.
  useEffect(() => {
    if (savedRef.current) return;
    if (status !== 'won' && status !== 'lost') return;
    if (!puzzle) return;
    savedRef.current = true;
    // The puzzle is over, so there is no partial state left to resume.
    clearNTGSrProgress();
    stampNTGSrPuzzleUsed(puzzle.id);
    const won = status === 'won';
    const hintsUsed = won ? Math.min(wrongGuesses.length, 2) : 2;
    (async () => {
      const result = await saveNTGSrDailyResult(won, hintsUsed, currentUser, puzzle.id);
      setSavedResult(result || null);
      if (result) onDailyComplete?.(result.streak, result.ironmanStreak, result.strikes);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  // Build the autocomplete universe from the three bundled rosters.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const texts = await Promise.all(
          [nflCsvUrl, nbaCsvUrl, moshCsvUrl].map(async (url) => {
            const res = await fetch(url);
            return res.text();
          })
        );
        if (!alive) return;
        const names = new Set();
        for (const text of texts) {
          const lines = text.trim().split('\n').slice(1);
          for (const line of lines) {
            const name = line.split(',')[0]?.trim();
            if (name) names.add(name);
          }
        }
        setUniverse(Array.from(names));
      } catch (e) {
        // Autocomplete is an assist; the game works without it.
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const suggestions =
    status === 'playing' && showSuggestions ? suggest(input, universe) : [];

  const submitGuess = (raw) => {
    const guess = (raw ?? input).trim();
    if (!guess || status !== 'playing') return;

    const result = matchGuess(guess, puzzle.answer, puzzle.aliases);
    if (result.correct) {
      setStatus('won');
      setWonInfo({ hintsUsed: wrongGuesses.length, matchedOn: result.matchedOn });
      setShowSuggestions(false);
      return;
    }

    const nextWrong = [...wrongGuesses, guess];
    setWrongGuesses(nextWrong);
    // Persist on every miss so an exit, refresh or resume-reload comes back to
    // the same board instead of handing out a fresh set of three guesses.
    saveNTGSrProgress(nextWrong, puzzle.id);
    setInput('');
    setShowSuggestions(false);
    setFlash(true);
    setTimeout(() => setFlash(false), 200);
    if (nextWrong.length >= MAX_GUESSES) {
      setStatus('lost');
    } else if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  // Leave-confirm, mirroring GameBoard's daily exit prompt but WITHOUT its
  // penalty: on a 3-guess puzzle, "clear your progress" would hand back a fresh
  // board (a reward, not a cost), and a forced loss would spend a strike and a
  // streak on what is usually a dead battery or a back-swipe. Guesses persist
  // either way, so the second prompt is friction only - nothing is ever lost.
  const handleExit = () => {
    // Nothing in flight on the splash or the end cards: leave immediately.
    if (!started || status !== 'playing' || !puzzle) {
      onGoHome();
      return;
    }
    const priorExits = getNTGSrExits(puzzle.id);
    const message = priorExits >= 1
      ? "You've stepped away once already. Your guesses are still saved, but he's not going to name himself. Leave anyway?"
      : "Heads up - your guesses are saved. Come back and you'll pick up right where you left off.";
    if (!window.confirm(message)) return;
    bumpNTGSrExits(puzzle.id);
    onGoHome();
  };

  // Same navigator.share -> clipboard fallback as ResultsScreen's handleShare.
  const shareResult = async (won, hintsUsed) => {
    const text = ntgSrShareText(won, hintsUsed);
    try {
      if (navigator.share) await navigator.share({ title: 'Know Your Guy', text });
      else { await navigator.clipboard.writeText(text); alert('Copied to clipboard.'); }
    } catch {}
  };

  // Same shape as ResultsScreen's rain: 30 randomized drops, win-only render.
  // (Hook lives above the early returns - rules of hooks.)
  const rainDrops = useMemo(
    () =>
      Array.from({ length: 30 }, (_, i) => ({
        id: i,
        left: Math.random() * 100,
        delay: Math.random() * 3,
        duration: 2 + Math.random() * 3,
        size: 20 + Math.random() * 20,
        emoji: RAIN_EMOJIS[Math.floor(Math.random() * RAIN_EMOJIS.length)],
      })),
    []
  );

  // App only renders this screen with a puzzle row in hand; bare guard anyway.
  if (!puzzle) {
    return (
      <div className="gamex-page">
        <GameTopBar onGoHome={onGoHome} />
        <p className="gamex-opener-label">Loading today's guy...</p>
      </div>
    );
  }

  // Pre-BEGIN splash, mirroring Jr.'s round-break screen. Same screen either
  // way - only the headline, sub-line and button label swap when there is a
  // puzzle in progress, so a returning player is told their guesses survived.
  if (!started) {
    // Read at render, not in state: the component remounts on re-entry, so this
    // is always the freshest saved value. getNTGSrProgress enforces same-Pacific-
    // day + same-puzzle-id. The < MAX_GUESSES bound means a used-up board can
    // never present as resumable (a finished puzzle is also cleared on save and
    // caught earlier by the played-today lock).
    const resumeUsed = (getNTGSrProgress(puzzle.id)?.wrongGuesses || []).length;
    const resuming = resumeUsed > 0 && resumeUsed < MAX_GUESSES;
    return (
      <div className="round-break">
        <button className="btn-home-corner" onClick={onGoHome}>HOME</button>
        <div className="round-break-content">
          <div className="round-break-icon">👴</div>
          <div className="round-break-badge">DAILY</div>
          <h2 className="round-break-title">{resuming ? 'WELCOME BACK' : 'NAME THAT GUY SR.'}</h2>
          <p className="round-break-desc">{resuming
            ? `You've used ${resumeUsed} of ${MAX_GUESSES} guesses.`
            : 'One guy, three guesses. Each miss reveals a hint: college, then the resume. Guess him to keep your streak.'}</p>
          <button className="btn btn-begin" onClick={onBegin}>{resuming ? 'CONTINUE' : 'BEGIN'}</button>
          <div className="round-break-brand">knowyourguy.com</div>
        </div>
      </div>
    );
  }

  const revealedHints = puzzle.hints.slice(
    0,
    Math.min(wrongGuesses.length, puzzle.hints.length)
  );
  const guessesLeft = MAX_GUESSES - wrongGuesses.length;

  return (
    <div className="gamex-page">
      {status === 'won' && (
        <div className="football-rain" aria-hidden="true">
          {rainDrops.map((fb) => (
            <span
              key={fb.id}
              className="football-drop"
              style={{
                left: `${fb.left}%`,
                animationDelay: `${fb.delay}s`,
                animationDuration: `${fb.duration}s`,
                fontSize: `${fb.size}px`,
              }}
            >
              {fb.emoji}
            </span>
          ))}
        </div>
      )}
      <GameTopBar onGoHome={handleExit} />
      <ScoreBoard
        title={<>NAME THAT GUY <span style={{ color: '#ff3b30', fontWeight: 900 }}>SR.</span></>}
        rightLabel="1 Question"
        strikes={wrongGuesses.length}
        maxStrikes={MAX_GUESSES}
        correctAt={status === 'won' ? wrongGuesses.length : undefined}
      />

      <div className="gamex-opener">
        {puzzle.mode && <p className="gamex-opener-mode">{puzzle.mode}</p>}
        <p className="gamex-opener-text">{puzzle.opener}</p>
        {puzzle.openerNote && <p className="gamex-opener-note">{puzzle.openerNote}</p>}
        {status === 'playing' && (
          <div className="gamex-photo-frame">
            <BustSilhouette />
          </div>
        )}
        {puzzle.builtBy && (
          <p className="gamex-built-by">Crafted and submitted by... <strong>{puzzle.builtBy}</strong></p>
        )}
      </div>

      {status === 'playing' && (
        <form
          className="gamex-form"
          onSubmit={(e) => {
            e.preventDefault();
            submitGuess();
          }}
        >
          <div className="gamex-input-wrap">
            <input
              ref={inputRef}
              className={`typed-answer-input${flash ? ' incorrect' : ''}`}
              type="text"
              value={input}
              placeholder="Type your guess..."
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck="false"
              onChange={(e) => {
                setInput(e.target.value);
                setShowSuggestions(true);
              }}
              onFocus={() => setShowSuggestions(true)}
            />
            {suggestions.length > 0 && (
              <ul className="gamex-suggestions">
                {suggestions.map((name) => (
                  <li key={name}>
                    {/* Spelling assist only - selecting fills the input, raw text always submits */}
                    <button
                      type="button"
                      onMouseDown={(e) => {
                        e.preventDefault();
                        setInput(name);
                        setShowSuggestions(false);
                        if (inputRef.current) inputRef.current.focus();
                      }}
                    >
                      {name}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <button className="btn btn-primary gamex-submit" type="submit" disabled={!input.trim()}>
            Guess ({guessesLeft} {guessesLeft === 1 ? 'guess' : 'guesses'} remaining)
          </button>
        </form>
      )}

      {wrongGuesses.length > 0 && (
        <div className="gamex-chips">
          {wrongGuesses.map((g, i) => (
            <span key={`${g}-${i}`} className="gamex-guess-chip">
              {g}
            </span>
          ))}
        </div>
      )}

      {/* All hint slots render from the start so players know what's coming;
          unearned ones show a lock INSTEAD of the text (never blurred text -
          blurred content still sits in the DOM where select/inspect reads it). */}
      {puzzle.hints.map((h, i) =>
        i < revealedHints.length ? (
          <div key={h.label} className="gamex-hint">
            <p className="gamex-hint-label">{h.label}</p>
            <p className="gamex-hint-text">{h.text}</p>
          </div>
        ) : status === 'playing' ? (
          <div key={h.label} className="gamex-hint gamex-hint-locked">
            <p className="gamex-hint-label">{h.label}</p>
            <p className="gamex-hint-text gamex-hint-locked-text">🔒 Unlocks after miss {i + 1}</p>
          </div>
        ) : null
      )}

      {status === 'won' && (
        <div className="gamex-result-card gamex-won">
          <p className="gamex-result-title">You know your guy.</p>
          <img className="gamex-photo" src={puzzle.photoUrl} alt={puzzle.answer} width="175" height="127" draggable="false" />
          <p className="gamex-result-answer">{puzzle.answer}</p>
          {puzzle.revealLink && (
            <p className="gamex-result-detail" style={{ textTransform: 'none' }}>
              <a href={puzzle.revealLink.url} target="_blank" rel="noreferrer" style={{ color: 'var(--gold)', textDecoration: 'underline' }}>{puzzle.revealLink.label}</a>
            </p>
          )}
          <p className="gamex-result-detail">
            {wonInfo.hintsUsed === 0
              ? 'No hints needed. Cold-blooded.'
              : `Got it with ${wonInfo.hintsUsed} hint${wonInfo.hintsUsed === 1 ? '' : 's'} revealed.`}
          </p>
          {(claimedStreak ?? savedResult?.streak) > 0 && currentUser && (
            <p className="gamex-result-detail" style={{ color: 'var(--gold)' }}>{(claimedStreak ?? savedResult.streak)}-day streak</p>
          )}
          {!currentUser && (
            <div style={{ marginTop: '14px', textAlign: 'center' }}>
              <p style={{ fontFamily: 'var(--font-heading)', fontSize: '12px', color: 'var(--gold)', letterSpacing: '2px', marginBottom: '6px' }}>SIGN UP TO SAVE YOUR STREAK</p>
              <p style={{ fontFamily: 'var(--font-body)', fontSize: '11px', color: 'var(--text-dim)', marginBottom: '10px' }}>Create a free account and today's win counts toward your streak. You'll also receive The Handcrafted Daily (<a href="/sample-daily.html" target="_blank" rel="noreferrer" style={{ color: 'var(--gold)' }}>sample</a>) (opt out whenever)</p>
              <button className="btn btn-primary" style={{ marginTop: '0' }} onClick={onShowAuth}>
                SIGN UP...
              </button>
            </div>
          )}
          <button className="btn btn-primary" style={{ marginTop: '12px' }} onClick={() => shareResult(true, wonInfo.hintsUsed)}>
            BRAG A LITTLE
          </button>
          <NextGameNudge currentGame="sr" currentUser={currentUser} onPlayDaily={onPlayDaily} onShowAuth={onShowAuth} />
          <button onClick={() => (currentUser ? setShowSubmit(true) : onShowAuth())} style={{ background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'var(--font-heading)', fontSize: '11px', color: 'var(--gold)', letterSpacing: '1px', padding: '10px 8px 0 8px', display: 'block', margin: '4px auto 0' }}>
            Want to write the next <span style={{ color: '#ff3b30', fontWeight: 900 }}>SR.</span>? Send it Matt's way (click)
          </button>
          {!currentUser && (
            <p style={{ fontFamily: 'var(--font-body)', fontSize: '10px', color: 'var(--text-dim)', opacity: 0.75, textAlign: 'center', margin: '2px 0 0 0' }}>must be signed up to submit</p>
          )}
          <PartnerPromoCard />
        </div>
      )}

      {status === 'lost' && (
        <div className="gamex-result-card gamex-lost">
          <div className="results-banner loser" style={{ padding: '16px', marginBottom: '14px' }}>
            <h1 className="results-title" style={{ margin: 0 }}>TOUGH ONE</h1>
          </div>
          <p className="gamex-result-title">You don't know your guy.</p>
          <img className="gamex-photo" src={puzzle.photoUrl} alt={puzzle.answer} width="175" height="127" draggable="false" />
          <p className="gamex-result-answer">{puzzle.answer}</p>
          {puzzle.revealLink && (
            <p className="gamex-result-detail" style={{ textTransform: 'none' }}>
              <a href={puzzle.revealLink.url} target="_blank" rel="noreferrer" style={{ color: 'var(--gold)', textDecoration: 'underline' }}>{puzzle.revealLink.label}</a>
            </p>
          )}
          {/* Standard loss line reused verbatim from the dailies (ResultsScreen) */}
          <p className="gamex-result-detail">Come back tomorrow.</p>
          {currentUser && savedResult?.strikes !== undefined && (
            <p className="gamex-result-detail" style={{ textTransform: 'none' }}>
              stri<span style={{ color: '#ff3b30', fontWeight: 900, fontSize: '1.1em', verticalAlign: 'middle' }}>K</span>e: {savedResult.strikes >= 1 ? savedResult.strikes : '-'} <span style={{ color: 'var(--text-dim)', fontSize: '0.85em' }}>(3 strikes your streak resets)</span>
            </p>
          )}
          {!currentUser && (
            <div style={{ marginTop: '14px', textAlign: 'center' }}>
              <p style={{ fontFamily: 'var(--font-heading)', fontSize: '12px', color: 'var(--text-dim)', letterSpacing: '2px', marginBottom: '6px' }}>SIGN IN TO UNLOCK MORE</p>
              <p style={{ fontFamily: 'var(--font-body)', fontSize: '11px', color: 'var(--text-dim)', lineHeight: '1.7', marginBottom: '10px' }}>
                View the leaderboard<br />Challenge friends head to head<br />Track your daily streak and accuracy<br />Earn sweet, sweet pieces <span style={{ fontSize: '0.85em', opacity: 0.75 }}>(while supplies last)</span><br />📬 The Handcrafted Daily (<a href="/sample-daily.html" target="_blank" rel="noreferrer" style={{ color: 'var(--gold)' }}>sample</a>) (opt out whenever)
              </p>
              <button className="btn btn-primary" style={{ marginTop: '0' }} onClick={onShowAuth}>
                SIGN IN / SIGN UP
              </button>
            </div>
          )}
          <button className="btn btn-secondary" style={{ marginTop: '12px' }} onClick={() => shareResult(false, 0)}>
            SHARE THE PAIN
          </button>
          <NextGameNudge currentGame="sr" currentUser={currentUser} onPlayDaily={onPlayDaily} onShowAuth={onShowAuth} />
          <button onClick={() => (currentUser ? setShowSubmit(true) : onShowAuth())} style={{ background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'var(--font-heading)', fontSize: '11px', color: 'var(--gold)', letterSpacing: '1px', padding: '10px 8px 0 8px', display: 'block', margin: '4px auto 0' }}>
            Want to write the next <span style={{ color: '#ff3b30', fontWeight: 900 }}>SR.</span>? Send it Matt's way (click)
          </button>
          {!currentUser && (
            <p style={{ fontFamily: 'var(--font-body)', fontSize: '10px', color: 'var(--text-dim)', opacity: 0.75, textAlign: 'center', margin: '2px 0 0 0' }}>must be signed up to submit</p>
          )}
          <PartnerPromoCard />
        </div>
      )}
      {showSubmit && <SubmitSrModal onClose={() => setShowSubmit(false)} />}
    </div>
  );
};

export default GameX;
